export function getYearFromString(dateString) {
  const year = dateString.split("-")[0];
  return year;
}

/**
 * Normalize a date string to YYYY-MM-DD format.
 * Converts from API display format (MM/DD/YYYY) to ISO format (YYYY-MM-DD).
 *
 * @param {string} dateString - Date string in MM/DD/YYYY or YYYY-MM-DD format
 * @returns {string} - Date in YYYY-MM-DD format or empty string if invalid
 */
export function normalizeDateToISO(dateString) {
  if (!dateString || typeof dateString !== "string") {
    return "";
  }

  const trimmed = dateString.trim();
  if (!trimmed) {
    return "";
  }

  // If already in YYYY-MM-DD format, return as is
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  // Handle MM/DD/YYYY format (from backend API)
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmed)) {
    const [month, day, year] = trimmed.split("/");
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }

  return trimmed;
}

/**
 * Format a date string from ISO format (YYYY-MM-DD) to display format (MM/DD/YYYY).
 *
 * @param {string} dateString - Date string in YYYY-MM-DD or MM/DD/YYYY format
 * @returns {string} - Date in MM/DD/YYYY format or original string if invalid
 */
export function formatDateForDisplay(dateString) {
  if (!dateString || typeof dateString !== "string") {
    return "";
  }

  const trimmed = dateString.trim();
  if (!trimmed) {
    return "";
  }

  // If already in MM/DD/YYYY format, return as is
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmed)) {
    return trimmed;
  }

  // Handle YYYY-MM-DD format (ISO) and convert to MM/DD/YYYY
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const [year, month, day] = trimmed.split("-");
    return `${month}/${day}/${year}`;
  }

  return trimmed;
}

export // Helper to format relative time
const formatTime = (createdAt) => {
  if (!createdAt) return "Just now";
  // getRelativeTime expects an object with timestamp (in seconds) or year/month/day
  // If createdAt is a datetime string, convert to timestamp
  let timeObj;
  if (typeof createdAt === "string") {
    // ISO string - convert to timestamp
    const date = new Date(createdAt);
    timeObj = { timestamp: Math.floor(date.getTime() / 1000) };
  } else if (typeof createdAt === "number") {
    // Already a timestamp (assume seconds)
    timeObj = { timestamp: createdAt };
  } else if (createdAt.timestamp) {
    // Already in the expected format
    timeObj = createdAt;
  } else {
    return "Just now";
  }
  return getRelativeTime(timeObj);
};

export function getRelativeTime(date) {
  const now = new Date();
  // Use timestamp if available (UTC), otherwise fall back to day/month/year (local)
  let notificationDate;
  if (date.timestamp) {
    // Timestamp is in seconds, convert to milliseconds for Date constructor
    notificationDate = new Date(date.timestamp * 1000);
  } else {
    // Fallback to day/month/year (creates local timezone date)
    notificationDate = new Date(date.year, date.month - 1, date.day);
  }
  const diffInMs = now - notificationDate;
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  const diffInMonths = Math.floor(diffInDays / 30);
  const diffInYears = Math.floor(diffInDays / 365);

  if (diffInMinutes < 1) {
    return "Just now";
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes === 1 ? "" : "s"} ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours === 1 ? "" : "s"} ago`;
  } else if (diffInDays < 30) {
    return `${diffInDays} day${diffInDays === 1 ? "" : "s"} ago`;
  } else if (diffInMonths < 12) {
    return `${diffInMonths} month${diffInMonths === 1 ? "" : "s"} ago`;
  } else {
    return `${diffInYears} year${diffInYears === 1 ? "" : "s"} ago`;
  }
}

/**
 * Calculates the number of days from today until a future date.
 * * @param {string} futureDateString - Date string in MM/DD/YYYY or YYYY-MM-DD format
 * @returns {number|null} - Number of days in the future, or null if the date is invalid/in the past
 */
export function getFutureDays(futureDateString) {
  const isoDate = normalizeDateToISO(futureDateString);
  if (!isoDate) return null;

  // Create date objects (set both to midnight to compare full days accurately)
  const futureDate = new Date(isoDate);
  futureDate.setHours(0, 0, 0, 0);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const diffInMs = futureDate - today;

  // Convert milliseconds to days
  const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));

  return diffInDays;
}

/**
 * Formats a date string to "Month DD, YYYY" format (e.g., April 23, 2026).
 * * @param {string} dateString - Date string in YYYY-MM-DD or MM/DD/YYYY format
 * @returns {string} - Date in "Month DD, YYYY" format or original string if invalid
 */
export function formatDateToLongString(dateString) {
  const isoDate = normalizeDateToISO(dateString); // Uses your existing logic safely
  if (!isoDate) return "";

  const [year, month, day] = isoDate.split("-");

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const monthIndex = parseInt(month, 10) - 1;
  const monthName = months[monthIndex];

  // Returns format: April 23, 2026
  return `${monthName} ${parseInt(day, 10)}, ${year}`;
}

/**
 * Formats a Unix timestamp (seconds or milliseconds) to "Month DD, YYYY" format.
 * * @param {number} timestamp - Timestamp in seconds (Stripe) or milliseconds
 * @returns {string} - Date in "Month DD, YYYY" format (e.g., Apr 23, 2026)
 */
export function formatTimestampToLongString(timestamp) {
  if (!timestamp) return "N/A";

  // Si el timestamp viene en segundos (Stripe habitual), lo pasamos a milisegundos
  const isSeconds = timestamp < 1000000000000;
  const date = new Date(isSeconds ? timestamp * 1000 : timestamp);

  if (isNaN(date.getTime())) return "N/A";

  // Creamos el string formateado que tu 'normalizeDateToISO' sí entiende (YYYY-MM-DD)
  const isoString = `${date.getFullYear()}-${String(
    date.getMonth() + 1,
  ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

  return formatDateToLongString(isoString);
}

/**
 * Calculates ISO start and end dates based on calendar-bound periods.
 *
 * @param {string} period - '1M', '3M', '6M', '1Y', '2Y', 'ALL', 'this_month', 'last_month'
 * @returns {{ start_date: string|null, end_date: string|null }}
 */
export function getPeriodDateRange(period) {
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
    2,
    "0",
  )}-${String(now.getDate()).padStart(2, "0")}`;

  const formatDate = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  switch (period) {
    case "1M":
    case "this_month": {
      // 1st day of current month -> today
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start_date: formatDate(start), end_date: todayStr };
    }

    case "last_month": {
      // 1st day of last month -> last day of last month
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { start_date: formatDate(start), end_date: formatDate(end) };
    }

    case "3M": {
      // 1st day of 2 months ago -> today
      const start = new Date(now.getFullYear(), now.getMonth() - 2, 1);
      return { start_date: formatDate(start), end_date: todayStr };
    }

    case "6M": {
      // 1st day of 5 months ago -> today
      const start = new Date(now.getFullYear(), now.getMonth() - 5, 1);
      return { start_date: formatDate(start), end_date: todayStr };
    }

    case "1Y": {
      // Jan 1 of current year -> today
      const start = new Date(now.getFullYear(), 0, 1);
      return { start_date: formatDate(start), end_date: todayStr };
    }

    case "2Y": {
      // Jan 1 of previous year -> today
      const start = new Date(now.getFullYear() - 1, 0, 1);
      return { start_date: formatDate(start), end_date: todayStr };
    }

    case "ALL":
      return { start_date: null, end_date: null };

    default: {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start_date: formatDate(start), end_date: todayStr };
    }
  }
}

/**
 * Formats an ISO date string (YYYY-MM-DD) or MM/DD/YYYY to "Mon/YYYY" format (e.g., "Aug/2026").
 *
 * @param {string} dateString - Date string in YYYY-MM-DD or MM/DD/YYYY format
 * @returns {string} - Date in "Mon/YYYY" format or original string if invalid
 */
export function formatDateToMonthYear(dateString) {
  const isoDate = normalizeDateToISO(dateString);
  if (!isoDate) return dateString || "";

  const [year, month] = isoDate.split("-");
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const monthIndex = parseInt(month, 10) - 1;
  const monthName = months[monthIndex];

  if (!monthName || !year) return dateString;

  return `${monthName}/${year}`;
}
